import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Icon } from 'semantic-ui-react';

import { useProjectContext } from '~/hooks';
import { useProjectWorksheetContext } from '~/features/projectWizard/worksheets/project/providers/ProjectWorksheetProvider';
import { usePageHeaderContext } from '~/components/PageHeader/PageHeaderProvider';
import { knownErrors } from '~/schema/project';
import WorksheetSearch from './WorksheetSearch';

type KnownErrorCategories = Record<string, string[]>;

interface ErrorFieldEntry {
  sectionId: string;
  subsectionId: string;
  schemaKey: string;
}

export default function WorksheetActionBar() {
  const { config, expandAllSections, collapseAllSections, expandSection, setActionBarHeight } = useProjectWorksheetContext();
  const { projectErrors } = useProjectContext();
  const pageHeaderContext = usePageHeaderContext();

  const barRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const update = () => setActionBarHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      setActionBarHeight(0);
    };
  }, [setActionBarHeight]);

  const orderedErrors = useMemo<ErrorFieldEntry[]>(() => {
    const errorKeys = Object.keys(projectErrors ?? {});
    if (errorKeys.length === 0) return [];

    const entries: ErrorFieldEntry[] = [];
    const used = new Set<string>();

    config.forEach(section => {
      const categories = knownErrors[section.id] as KnownErrorCategories | undefined;
      if (!categories) return;
      section.subsections.forEach(sub => {
        const templates = categories[sub.id];
        if (!Array.isArray(templates)) return;

        const templateIdx = new Map<string, number>();
        templates.forEach((t, i) => templateIdx.set(t, i));

        // Collect real error keys for this subsection, sorted row-by-row then
        // by template order. e.g. storage.0.make, storage.0.model, …,
        // storage.1.make, storage.1.model.
        const matches = errorKeys
          .filter(key => !used.has(key))
          .map(key => {
            const template = key.replace(/\.\d+/g, '[]');
            return { key, template, templateIdx: templateIdx.get(template) ?? -1 };
          })
          .filter(m => m.templateIdx >= 0)
          .map(m => ({
            ...m,
            indexPath: (m.key.match(/\.\d+/g) ?? []).join(',')
          }))
          .sort((a, b) => {
            if (a.indexPath !== b.indexPath) {
              return a.indexPath.localeCompare(b.indexPath, undefined, { numeric: true });
            }
            return a.templateIdx - b.templateIdx;
          });

        matches.forEach(m => {
          used.add(m.key);
          entries.push({ sectionId: section.id, subsectionId: sub.id, schemaKey: m.key });
        });
      });
    });
    return entries;
  }, [projectErrors, config]);

  const findAnchor = useCallback((entry: ErrorFieldEntry) => {
    return (
      document.getElementById(`field-${entry.schemaKey}`) ??
      document.getElementById(`${entry.sectionId}-${entry.subsectionId}`) ??
      document.getElementById(entry.sectionId)
    );
  }, []);

  const lastVisitedRef = useRef<string | null>(null);

  const stepError = useCallback((delta: 1 | -1) => {
    if (orderedErrors.length === 0) return;

    const lastIdx = orderedErrors.findIndex(e => e.schemaKey === lastVisitedRef.current);
    let nextIdx: number;
    if (lastIdx === -1) {
      nextIdx = delta === 1 ? 0 : orderedErrors.length - 1;
    } else {
      nextIdx = (lastIdx + delta + orderedErrors.length) % orderedErrors.length;
    }
    const next = orderedErrors[nextIdx];
    lastVisitedRef.current = next.schemaKey;

    expandSection(next.sectionId);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const target = findAnchor(next);
        if (!target) return;

        // Account for every sticky element above the worksheet content:
        // mini-header + action bar + section heading. scrollIntoView's
        // scroll-margin can only budget for the first two; section headings
        // are also sticky and would otherwise cover the focused field.
        const miniHeaderH = pageHeaderContext.height ?? 0;
        const actionBarH = barRef.current?.getBoundingClientRect().height ?? 0;
        const heading = target.closest('.worksheet-body__section')
          ?.querySelector<HTMLElement>('.worksheet-body__section-heading');
        const headingH = heading?.getBoundingClientRect().height ?? 0;
        const offset = miniHeaderH + actionBarH + headingH + 12;

        const targetTop = target.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: targetTop, behavior: 'smooth' });

        const focusable = target.querySelector<HTMLElement>(
          'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"]'
        );
        focusable?.focus({ preventScroll: true });
      });
    });
  }, [orderedErrors, expandSection, findAnchor, pageHeaderContext.height]);

  const hasErrors = orderedErrors.length > 0;

  return (
    <div className="card worksheet-body__search" style={{ top: `${(pageHeaderContext.height ?? 0) / 16}rem` }} ref={barRef}>
      <WorksheetSearch />
      <div className="worksheet-body__search-actions">
        <div className="worksheet-body__error-nav" role="group" aria-label="Step through validation errors">
          <button
            type="button"
            className="worksheet-body__error-nav-step"
            onClick={() => stepError(-1)}
            disabled={!hasErrors}
            aria-label="Previous error"
          >
            <Icon name="chevron left" />
          </button>
          <span className="worksheet-body__error-nav-label">
            <span className={`field-status-circle ${hasErrors ? 'has-errors' : 'success'}`}>
              {orderedErrors.length}
            </span>
            Needs attention
          </span>
          <button
            type="button"
            className="worksheet-body__error-nav-step"
            onClick={() => stepError(1)}
            disabled={!hasErrors}
            aria-label="Next error"
          >
            <Icon name="chevron right" />
          </button>
        </div>
        <button
          type="button"
          className="worksheet-body__search-action"
          onClick={expandAllSections}
        >
          <Icon name="chevron down" /> Expand all
        </button>
        <button
          type="button"
          className="worksheet-body__search-action"
          onClick={collapseAllSections}
        >
          <Icon name="chevron right" /> Collapse all
        </button>
      </div>
    </div>
  );
}
